import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@bonanza-social/toolkit/hooks';
import { Box, ScrollView, SearchInput } from '@bonanza-social/moonlight';
import { SearchProps } from './Search.types';
import MostSearched from './MostSearched/MostSearched';
import TopSlotProviders from './TopSlotProviders/TopSlotProviders';
import Results from './Results/Results';
import { useFocusEffect, useTransitionEnd } from '@bonanza-social/navigation';
import { TextInput } from 'react-native';
import { trackEvent, useDispatch } from '@bonanza-social/toolkit/state';
import { fetchPromotedContent } from '../../state';
import { useTranslation } from '@bonanza-social/i18n';

function Search(props: SearchProps) {
  const searchRef = useRef<TextInput>(null);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebouncedValue(searchText);
  const transitionEnded = useTransitionEnd();
  const { t } = useTranslation('games');
  const dispatch = useDispatch();

  useEffect(() => {
    if (debouncedSearchText) {
      dispatch(
        trackEvent({
          name: 'games_search_input',
          params: {
            inputLabel: t('find'),
            inputValue: debouncedSearchText,
            inputTrackingId: 'games_search_input',
          },
        }),
      );
    }
  }, [debouncedSearchText, dispatch, t]);

  useEffect(() => {
    if (transitionEnded) {
      searchRef.current?.focus();
    }
  }, [transitionEnded]);

  useFocusEffect(
    useCallback(() => {
      dispatch(fetchPromotedContent());
    }, [dispatch]),
  );

  return (
    <Box flex={1} {...props}>
      <SearchInput
        ref={searchRef}
        value={searchText}
        placeholderKey="find"
        placeholderNS="games"
        onChangeText={setSearchText}
        marginHorizontal="space-600"
        marginBottom="space-400"
        trackingId="games_search_input"
      />
      {debouncedSearchText ? (
        <Results searchText={debouncedSearchText} />
      ) : (
        <ScrollView
          keyboardDismissMode="interactive"
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{
            gap: 'space-600',
            height: '100%',
          }}
        >
          <MostSearched paddingTop="space-200" />
          <TopSlotProviders />
        </ScrollView>
      )}
    </Box>
  );
}

export default memo(Search);
